/* External */
import { datadogRum } from '@datadog/browser-rum';

/* Utils */
import {
  APP_VERSION,
  BACKEND_SERVICE_HOST,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SAMPLE_RATE,
  DATADOG_SERVICE,
  ENVIRONMENT,
  AUTH_SERVICE_HOST,
} from './env';
import platform from './platform';
import { addAccessTokenDataSubscriber, getUserAccount } from './accessTokenData';

function initDatadog() {
  if (import.meta.env.VITE_IS_DATADOG_ACTIVE === 'true') {
    datadogRum.init({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: DATADOG_SERVICE,
      sessionSampleRate: DATADOG_SAMPLE_RATE ?? 100,
      sessionReplaySampleRate: DATADOG_SAMPLE_RATE ?? 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      env: ENVIRONMENT || 'local',
      version: APP_VERSION,
      useCrossSiteSessionCookie: platform.datadog.useCrossSiteSessionCookie,
      allowFallbackToLocalStorage: true,
      allowedTracingUrls: [
        BACKEND_SERVICE_HOST,
        AUTH_SERVICE_HOST,
        import.meta.env.VITE_TOOLBOX_HOST,
      ],
      beforeSend,
    });
    setDatadogUser();
    listenForUserChanges();
  }
}

/**
 * @param {import('@datadog/browser-rum').RumEvent} event
 * @param {import('@datadog/browser-rum').RumEventDomainContext} context
 * @returns {boolean}
 */
function beforeSend(event, context) {
  const additionalProps = {};
  if (event.type === 'resource') {
    if (event.resource.type === 'fetch') {
      additionalProps.requestHeaders = context.requestInit?.headers;
      additionalProps.requestBody = context.requestInit?.body;
      additionalProps.responseHeaders = context.response?.headers;
    }

    if (event.resource.type === 'xhr') {
      additionalProps.responseHeaders = rawHttpHeadersToObject(
        context.xhr?.getAllResponseHeaders(),
      );
    }
  }
  // eslint-disable-next-line no-param-reassign -- we need to mutate the datadog event object
  event.context = {
    ...event.context,
    ...additionalProps,
  };
  return true;
}

function rawHttpHeadersToObject(headers) {
  return Object.fromEntries(
    headers?.trim().split(/[\n\r]+/)
      .map(
        (header) => header.split(':').map((keyOrValue) => keyOrValue.trim()),
      )
      .filter(([key, value]) => !!key && !!value),
  );
}

async function setDatadogUser() {
  const user = await getUserAccount();
  if (user) {
    const {
      uid,
      username,
      accountType: loginType,
      firstName,
      lastName,
    } = user;

    datadogRum.setUser({
      id: uid,
      name: `${firstName || ''} ${lastName || ''}`,
      email: username,
      loginType,
    });
  } else {
    datadogRum.clearUser();
  }
}

function listenForUserChanges() {
  addAccessTokenDataSubscriber(setDatadogUser);
}

initDatadog();
